import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Cast,
  Loader2,
  Maximize,
  Minimize,
  Pause,
  Play,
  PictureInPicture2,
  RotateCcw,
  Settings as SettingsIcon,
  Share2,
  Subtitles,
  Volume2,
  VolumeX,
} from 'lucide-react';
import SeekBar, { type Chapter, type SpriteMeta } from './SeekBar';
import OdenSettingsMenu, {
  type AudioOption,
  type CaptionOption,
  type PlaybackStats,
  type QualityOption,
  type SettingsView,
} from './OdenSettingsMenu';
import OdenCaptionsMenu, { type CaptionTrack } from './OdenCaptionsMenu';
import OdenShortcuts from './OdenShortcuts';
import VolumeBar from './VolumeBar';
import { useAmbientColor } from './useAmbientColor';
import { useReducedMotion } from './useReducedMotion';
import './odenPlayer.css';

export type { SpriteMeta, Chapter, CaptionTrack, QualityOption, AudioOption };

export type PlayerTheme = 'dark' | 'light' | 'system';

export interface NextUpEntry {
  id: string;
  title: string;
  subtitle?: string;
  posterUrl?: string;
  onClick?: () => void;
}

export interface OdenPlayerProps {
  /** Required playback source. */
  src: string;
  contentType?: string | null;

  /** Optional metadata. Title shows in the top bar (when `showControls.title !== false`). */
  title?: string;
  subtitle?: string;
  /** Image URL for the idle / poster state. Useful when the video isn't ready yet. */
  poster?: string;

  // Theme
  /** 'system' resolves dynamically via `prefers-color-scheme`. */
  theme?: PlayerTheme;
  /** CSS color string. Default NNi primary purple. */
  accent?: string;
  /** Outer border-radius in px. Default 18. */
  radius?: number;

  // Playback options
  speeds?: number[];
  defaultSpeed?: number;
  autoPlay?: boolean;

  // Optional features — each is hidden if no data / no callback is supplied
  qualities?: QualityOption[];
  defaultQuality?: string;
  onSelectQuality?: (id: string) => void;

  audioTracks?: AudioOption[];
  defaultAudio?: string;
  onSelectAudio?: (id: string) => void;

  /** Pre-supplied caption tracks. Each rendered track is `<track src=...>` with kind=subtitles. */
  captions?: Array<CaptionTrack & { src: string; default?: boolean }>;

  chapters?: Chapter[];

  sprite?: SpriteMeta | null;

  /** Per-control visibility. Each key defaults to `true` and is overridden by auto-detect rules below:
   *  - `cc`: hidden when no captions are available
   *  - `pip`: hidden when the browser doesn't support it
   *  - `cast`: hidden unless `onCast` is provided
   *  - `share`: hidden unless `onShare` is provided OR `navigator.share` exists
   *  - `audio`: hidden when fewer than 2 tracks
   */
  showControls?: {
    play?: boolean; scrub?: boolean; vol?: boolean; time?: boolean;
    cc?: boolean; settings?: boolean; pip?: boolean; fs?: boolean;
    cast?: boolean; share?: boolean; back10?: boolean; fwd10?: boolean;
    brand?: boolean; title?: boolean;
  };

  // Optional callbacks
  onCast?: () => void;
  onShare?: () => void;
  onEnded?: () => void;
  onPlayNext?: (id: string) => void;

  // Ended-state
  nextUp?: NextUpEntry[];
  autoPlayNextSec?: number;

  /** YouTube-style ambient color tint behind the video. Default `true`.
   *  Auto-disables under `prefers-reduced-motion` and when the canvas is
   *  tainted by cross-origin video bytes. */
  ambientMode?: boolean;

  /** Fires once after mount with the internal `<video>` element, and again
   *  with `null` on unmount. Used by HLS / DASH wrappers (hls.js, shaka)
   *  to attach to the element without forking the player. */
  onVideoReady?: (el: HTMLVideoElement | null) => void;
}

const DEFAULT_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const IDLE_HIDE_MS = 2500;
const DEFAULT_ACCENT = '#6641EF';

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Cross-fade-with-rotation between play and pause glyphs. Matches the
 * AddNewButton ↔ X morph language used elsewhere in NNi Drive: outgoing
 * icon fades + rotates 15°, incoming fades from -15° to 0. Reads as a
 * morph at 200 ms; reduced-motion CSS in `index.css` zeroes the transition
 * to make the swap instant.
 */
function MorphPlayPause({ playing, size = 18 }: { playing: boolean; size?: number }) {
  const layerStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 200ms ease-out, transform 200ms ease-out',
  };
  return (
    <span style={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}>
      <span
        style={{
          ...layerStyle,
          opacity: playing ? 0 : 1,
          transform: playing ? 'rotate(15deg) scale(.85)' : 'rotate(0deg) scale(1)',
        }}
      >
        <Play size={size} fill="currentColor" />
      </span>
      <span
        style={{
          ...layerStyle,
          opacity: playing ? 1 : 0,
          transform: playing ? 'rotate(0deg) scale(1)' : 'rotate(-15deg) scale(.85)',
        }}
      >
        <Pause size={size} fill="currentColor" />
      </span>
    </span>
  );
}

function Back5Icon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3 3v4h4" />
      <text x="12" y="15.5" textAnchor="middle" fontSize="7.5" fontWeight="700" fill="currentColor" stroke="none">5</text>
    </svg>
  );
}
function Fwd5Icon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 12a8.5 8.5 0 1 1-2.6-6.1" />
      <path d="M21 3v4h-4" />
      <text x="12" y="15.5" textAnchor="middle" fontSize="7.5" fontWeight="700" fill="currentColor" stroke="none">5</text>
    </svg>
  );
}

interface GlassBtnProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  active?: boolean;
  label: string;
  /** Optional keyboard hint shown alongside the tooltip label (e.g. "K", "←"). */
  shortcut?: string;
  /** Where the tooltip sits relative to the button. Default 'top' suits
   *  bottom-chrome buttons; the top-bar buttons should pass 'bottom'. */
  tooltipPlacement?: 'top' | 'bottom';
  onClick?: () => void;
}

const GlassBtn: React.FC<GlassBtnProps> = ({
  children,
  size = 'md',
  active,
  label,
  shortcut,
  tooltipPlacement = 'top',
  onClick,
}) => {
  const dim = size === 'lg' ? 56 : size === 'sm' ? 28 : 36;
  return (
    <span className={`oden-tip-wrap oden-tip-${tooltipPlacement}`}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className="oden-glassbtn"
        data-active={active ? '1' : '0'}
        style={{ width: dim, height: dim }}
      >
        <span style={{ width: dim * 0.5, height: dim * 0.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {children}
        </span>
      </button>
      <span className="oden-tip" role="tooltip">
        <span className="oden-tip-label">{label}</span>
        {shortcut && <span className="oden-tip-kbd">{shortcut}</span>}
      </span>
    </span>
  );
};

// Resolve 'system' theme to an actual scheme using prefers-color-scheme.
function useResolvedTheme(theme: PlayerTheme): 'dark' | 'light' {
  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [theme]);
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return systemDark ? 'dark' : 'light';
}

const OdenPlayer: React.FC<OdenPlayerProps> = ({
  src,
  contentType,
  title,
  subtitle,
  poster,
  theme = 'system',
  accent = DEFAULT_ACCENT,
  radius = 18,
  speeds = DEFAULT_SPEEDS,
  defaultSpeed = 1,
  autoPlay = false,
  qualities,
  defaultQuality,
  onSelectQuality,
  audioTracks: audioTracksProp,
  defaultAudio,
  onSelectAudio,
  captions,
  chapters,
  sprite,
  showControls,
  onCast,
  onShare,
  onEnded,
  onPlayNext,
  nextUp,
  autoPlayNextSec = 12,
  ambientMode = true,
  onVideoReady,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const idleTimerRef = useRef<number | null>(null);
  const menuAnchorRef = useRef<HTMLDivElement>(null);

  const reducedMotion = useReducedMotion();
  const ambientEnabled = ambientMode && !reducedMotion;
  const { color: ambientColor, tainted: ambientTainted } = useAmbientColor(videoRef, ambientEnabled);
  const ambientActive = ambientEnabled && !ambientTainted && ambientColor !== null;

  // Surface the internal <video> element to wrappers that need direct access
  // (hls.js attachment, custom AirPlay attribute toggling, Cast hand-off).
  // The callback is intentionally fired in an effect so consumers see the
  // committed DOM element, not a transient render-time ref.
  useEffect(() => {
    if (!onVideoReady) return;
    onVideoReady(videoRef.current);
    return () => onVideoReady(null);
  }, [onVideoReady]);

  // Media state
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(defaultSpeed);
  const [endedShown, setEndedShown] = useState(false);

  // Captions
  const [activeCaption, setActiveCaption] = useState<string | null>(
    captions?.find((c) => c.default)?.id ?? null,
  );
  const [captionLine, setCaptionLine] = useState<string | null>(null);

  // Quality / audio (caller owns the side effect via callbacks)
  const [activeQuality, setActiveQuality] = useState<string | undefined>(defaultQuality);
  const [activeAudio, setActiveAudio] = useState<string | undefined>(defaultAudio);

  // Chrome / overlays
  const [controlsVisible, setControlsVisible] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPip, setIsPip] = useState(false);

  // Menus
  const [menuOpen, setMenuOpen] = useState<'none' | 'settings' | 'captions'>('none');
  const [settingsView, setSettingsView] = useState<SettingsView>('root');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Stats sampling
  const [stats, setStats] = useState<PlaybackStats>({});

  // Auto-play next countdown
  const [autoNextRemaining, setAutoNextRemaining] = useState(0);

  // Double-tap seek hint (YouTube-style ripple on left/right halves)
  const [doubleTapHint, setDoubleTapHint] = useState<{ side: 'left' | 'right'; key: number } | null>(null);
  const lastClickRef = useRef<{ time: number; x: number } | null>(null);
  const singleClickTimerRef = useRef<number | null>(null);

  const sourceType = contentType && contentType.startsWith('video/') ? contentType : undefined;
  const resolvedTheme = useResolvedTheme(theme);

  // ── Auto-detect feature availability ────────────────────────────────────
  const pipSupported = typeof document !== 'undefined' && (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled === true;
  const shareSupported = typeof navigator !== 'undefined' && typeof (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share === 'function';
  const ccAvailable = !!(captions && captions.length > 0);
  const qualityAvailable = !!(qualities && qualities.length > 0);

  const sc = useMemo(() => ({
    play: showControls?.play ?? true,
    scrub: showControls?.scrub ?? true,
    vol: showControls?.vol ?? true,
    time: showControls?.time ?? true,
    cc: (showControls?.cc ?? true) && ccAvailable,
    settings: showControls?.settings ?? true,
    pip: (showControls?.pip ?? true) && pipSupported,
    fs: showControls?.fs ?? true,
    cast: (showControls?.cast ?? true) && !!onCast,
    share: (showControls?.share ?? true) && (!!onShare || shareSupported),
    back10: showControls?.back10 ?? true,
    fwd10: showControls?.fwd10 ?? true,
    brand: showControls?.brand ?? false,
    title: showControls?.title ?? !!title,
  }), [showControls, ccAvailable, pipSupported, shareSupported, onCast, onShare, title]);

  // ── Imperative video controls ───────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const seekBy = useCallback((deltaSec: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + deltaSec));
  }, []);

  const seekTo = useCallback((time: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || time, time));
  }, []);

  const adjustVolume = useCallback((delta: number) => {
    const v = videoRef.current;
    if (!v) return;
    const next = Math.max(0, Math.min(1, (v.volume || 0) + delta));
    v.volume = next;
    if (next > 0) v.muted = false;
  }, []);

  const setVolumeAbs = useCallback((next: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = Math.max(0, Math.min(1, next));
    if (next > 0) v.muted = false;
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  }, []);

  const setRate = useCallback((rate: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = rate;
    setPlaybackRate(rate);
  }, []);

  const adjustRate = useCallback((delta: number) => {
    const idx = speeds.indexOf(playbackRate);
    if (idx === -1) {
      setRate(1);
      return;
    }
    const next = Math.max(0, Math.min(speeds.length - 1, idx + (delta > 0 ? 1 : -1)));
    setRate(speeds[next]);
  }, [playbackRate, speeds, setRate]);

  const toggleFullscreen = useCallback(async () => {
    const wrapper = wrapperRef.current;
    const v = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!wrapper) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const active = doc.fullscreenElement || doc.webkitFullscreenElement;
    try {
      if (active) {
        if (doc.exitFullscreen) await doc.exitFullscreen();
        else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
      } else if (wrapper.requestFullscreen) {
        await wrapper.requestFullscreen();
      } else if (v?.webkitEnterFullscreen) {
        v.webkitEnterFullscreen();
      }
    } catch {
      /* fullscreen denied / unsupported */
    }
  }, []);

  const togglePip = useCallback(async () => {
    if (!pipSupported) return;
    const v = videoRef.current;
    if (!v) return;
    const doc = document as Document & { pictureInPictureElement?: Element | null };
    try {
      if (doc.pictureInPictureElement) {
        await (document as Document & { exitPictureInPicture?: () => Promise<void> }).exitPictureInPicture?.();
      } else {
        await (v as HTMLVideoElement & { requestPictureInPicture?: () => Promise<PictureInPictureWindow> }).requestPictureInPicture?.();
      }
    } catch {
      /* pip denied / unsupported */
    }
  }, [pipSupported]);

  const handleCast = useCallback(() => {
    if (onCast) onCast();
  }, [onCast]);

  const handleShare = useCallback(async () => {
    if (onShare) {
      onShare();
      return;
    }
    if (!shareSupported) return;
    try {
      await (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share?.({
        title: title || undefined,
        text: subtitle || undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      });
    } catch {
      /* user cancelled or unsupported */
    }
  }, [onShare, shareSupported, title, subtitle]);

  const handleReplay = useCallback(() => {
    setEndedShown(false);
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  }, []);

  // ── Idle controls timer ─────────────────────────────────────────────────
  const bumpControls = useCallback(() => {
    setControlsVisible(true);
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => setControlsVisible(false), IDLE_HIDE_MS);
  }, []);

  useEffect(() => {
    bumpControls();
    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    };
  }, [bumpControls]);

  // ── Double-tap seek ─────────────────────────────────────────────────────
  // Single click on the video toggles play; a second click within 280 ms on
  // the same side seeks ±5s and fires a ripple animation. Tap location
  // matters: left half rewinds, right half fast-forwards.
  const handleVideoClick = useCallback((e: React.MouseEvent<HTMLVideoElement>) => {
    bumpControls();
    const now = performance.now();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const halfway = rect.width / 2;
    const last = lastClickRef.current;
    const isDouble = !!last && now - last.time < 280;

    if (isDouble) {
      if (singleClickTimerRef.current) {
        window.clearTimeout(singleClickTimerRef.current);
        singleClickTimerRef.current = null;
      }
      lastClickRef.current = null;
      if (x < halfway) {
        seekBy(-5);
        setDoubleTapHint({ side: 'left', key: now });
      } else {
        seekBy(5);
        setDoubleTapHint({ side: 'right', key: now });
      }
      return;
    }

    lastClickRef.current = { time: now, x };
    if (singleClickTimerRef.current) window.clearTimeout(singleClickTimerRef.current);
    singleClickTimerRef.current = window.setTimeout(() => {
      togglePlay();
      singleClickTimerRef.current = null;
      lastClickRef.current = null;
    }, 240);
  }, [bumpControls, seekBy, togglePlay]);

  useEffect(() => () => {
    if (singleClickTimerRef.current) window.clearTimeout(singleClickTimerRef.current);
  }, []);

  useEffect(() => {
    if (!doubleTapHint) return;
    const id = window.setTimeout(() => setDoubleTapHint(null), 600);
    return () => window.clearTimeout(id);
  }, [doubleTapHint]);

  // Force-show chrome while paused, scrubbing, before first play, a menu is
  // open, or the shortcuts overlay is up. Mere cursor presence is NOT enough
  // — only actual movement (via `onMouseMove` → bumpControls) extends the
  // idle timer. Otherwise the chrome would never hide as long as the cursor
  // sat anywhere inside the player.
  const menusForceVisible = menuOpen !== 'none' || shortcutsOpen;
  const showChrome = controlsVisible || !playing || scrubbing || menusForceVisible;

  // ── Keyboard ────────────────────────────────────────────────────────────
  const closeAllMenus = useCallback(() => {
    setMenuOpen('none');
    setSettingsView('root');
    setShortcutsOpen(false);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Esc closes menus first, then exits fullscreen.
    if (e.key === 'Escape') {
      if (menuOpen !== 'none' || shortcutsOpen) {
        e.preventDefault();
        closeAllMenus();
        return;
      }
      // Let the browser handle fullscreen exit.
      return;
    }
    if (e.key === '?') {
      e.preventDefault();
      setShortcutsOpen((v) => !v);
      bumpControls();
      return;
    }
    if (e.shiftKey && (e.key === '+' || e.key === '=')) {
      e.preventDefault();
      adjustRate(1);
      bumpControls();
      return;
    }
    if (e.shiftKey && (e.key === '-' || e.key === '_')) {
      e.preventDefault();
      adjustRate(-1);
      bumpControls();
      return;
    }
    if (e.key >= '0' && e.key <= '9' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const digit = Number(e.key);
      if (duration > 0) {
        e.preventDefault();
        seekTo(duration * (digit / 10));
        bumpControls();
      }
      return;
    }
    switch (e.key) {
      case ' ':
      case 'k':
      case 'K':
        e.preventDefault();
        togglePlay();
        bumpControls();
        return;
      case 'j':
      case 'J':
        e.preventDefault();
        seekBy(-10);
        bumpControls();
        return;
      case 'l':
      case 'L':
        e.preventDefault();
        seekBy(10);
        bumpControls();
        return;
      case 'ArrowLeft':
        e.preventDefault();
        seekBy(-5);
        bumpControls();
        return;
      case 'ArrowRight':
        e.preventDefault();
        seekBy(5);
        bumpControls();
        return;
      case 'ArrowUp':
        e.preventDefault();
        adjustVolume(0.1);
        bumpControls();
        return;
      case 'ArrowDown':
        e.preventDefault();
        adjustVolume(-0.1);
        bumpControls();
        return;
      case 'm':
      case 'M':
        e.preventDefault();
        toggleMute();
        bumpControls();
        return;
      case 'f':
      case 'F':
        e.preventDefault();
        toggleFullscreen();
        bumpControls();
        return;
      case 'i':
      case 'I':
        if (sc.pip) {
          e.preventDefault();
          togglePip();
          bumpControls();
        }
        return;
      case 'c':
      case 'C':
        if (sc.cc) {
          e.preventDefault();
          setMenuOpen((m) => (m === 'captions' ? 'none' : 'captions'));
          bumpControls();
        }
        return;
      case 'q':
      case 'Q':
        if (qualityAvailable) {
          e.preventDefault();
          setMenuOpen('settings');
          setSettingsView('quality');
          bumpControls();
        }
        return;
    }
  };

  // ── Wire <video> media events ───────────────────────────────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => setDuration(v.duration || 0);
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onProgress = () => {
      const b = v.buffered;
      if (b.length > 0) setBuffered(b.end(b.length - 1));
    };
    const onPlay = () => {
      setPlaying(true);
      setStarted(true);
      setEndedShown(false);
      bumpControls();
    };
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    const onVolume = () => {
      setVolume(v.volume);
      setMuted(v.muted);
    };
    const onRate = () => setPlaybackRate(v.playbackRate);
    const onEndedEvt = () => {
      setEndedShown(true);
      setPlaying(false);
      onEnded?.();
    };
    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('durationchange', onLoaded);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('progress', onProgress);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);
    v.addEventListener('volumechange', onVolume);
    v.addEventListener('ratechange', onRate);
    v.addEventListener('ended', onEndedEvt);
    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('durationchange', onLoaded);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('progress', onProgress);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('volumechange', onVolume);
      v.removeEventListener('ratechange', onRate);
      v.removeEventListener('ended', onEndedEvt);
    };
  }, [bumpControls, onEnded]);

  // Apply default playback rate once metadata is ready.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = defaultSpeed;
  }, [defaultSpeed]);

  // ── Captions: toggle track modes, listen for cue changes ─────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !captions || captions.length === 0) return;
    const tracks = v.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i] as TextTrack & { id?: string };
      // Cross-reference by index since `<track>` elements get a stable order.
      const cap = captions[i];
      if (!cap) continue;
      t.mode = activeCaption === cap.id ? 'showing' : 'disabled';
    }
  }, [captions, activeCaption]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handleCue = () => {
      const tracks = v.textTracks;
      for (let i = 0; i < tracks.length; i++) {
        const t = tracks[i];
        if (t.mode === 'showing' && t.activeCues && t.activeCues.length > 0) {
          // Concatenate cues using a newline; a single one is the common case.
          const text = Array.from(t.activeCues)
            .map((c) => (c as VTTCue).text || '')
            .filter(Boolean)
            .join('\n');
          setCaptionLine(text || null);
          return;
        }
      }
      setCaptionLine(null);
    };
    const tracks = v.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].addEventListener('cuechange', handleCue);
    }
    handleCue();
    return () => {
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].removeEventListener('cuechange', handleCue);
      }
    };
  }, [captions]);

  // ── Fullscreen + PiP state listeners ────────────────────────────────────
  useEffect(() => {
    const onFs = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      setIsFullscreen(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
    };
    const onPip = () => {
      const doc = document as Document & { pictureInPictureElement?: Element | null };
      setIsPip(!!doc.pictureInPictureElement);
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs as EventListener);
    const v = videoRef.current;
    if (v) {
      v.addEventListener('enterpictureinpicture', onPip);
      v.addEventListener('leavepictureinpicture', onPip);
    }
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs as EventListener);
      if (v) {
        v.removeEventListener('enterpictureinpicture', onPip);
        v.removeEventListener('leavepictureinpicture', onPip);
      }
    };
  }, []);

  // ── Pause + exit fullscreen on unmount ──────────────────────────────────
  useEffect(() => {
    return () => {
      const doc = document as Document & {
        webkitFullscreenElement?: Element | null;
        webkitExitFullscreen?: () => Promise<void>;
        pictureInPictureElement?: Element | null;
        exitPictureInPicture?: () => Promise<void>;
      };
      try {
        videoRef.current?.pause();
      } catch {
        /* element may already be detached */
      }
      try {
        if (doc.fullscreenElement && doc.exitFullscreen) doc.exitFullscreen();
        else if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) doc.webkitExitFullscreen();
        if (doc.pictureInPictureElement && doc.exitPictureInPicture) doc.exitPictureInPicture();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // ── Stats sampling — runs while the stats panel is open ─────────────────
  useEffect(() => {
    if (menuOpen !== 'settings' || settingsView !== 'stats') return;
    const tick = () => {
      const v = videoRef.current;
      if (!v) return;
      const b = v.buffered;
      const bufEnd = b.length > 0 ? b.end(b.length - 1) : 0;
      const bufferAheadSec = Math.max(0, bufEnd - v.currentTime);
      const quality = (v as HTMLVideoElement & { getVideoPlaybackQuality?: () => { droppedVideoFrames?: number; totalVideoFrames?: number } }).getVideoPlaybackQuality?.();
      setStats({
        resolution: v.videoWidth && v.videoHeight ? `${v.videoWidth}×${v.videoHeight}` : undefined,
        durationSec: v.duration,
        currentSec: v.currentTime,
        bufferAheadSec,
        playbackRate: v.playbackRate,
        volume: v.volume,
        muted: v.muted,
        droppedFrames: quality?.droppedVideoFrames,
        totalFrames: quality?.totalVideoFrames,
        readyState: v.readyState,
        networkState: v.networkState,
      });
    };
    tick();
    const id = window.setInterval(tick, 750);
    return () => window.clearInterval(id);
  }, [menuOpen, settingsView]);

  // ── Click outside menu to close ─────────────────────────────────────────
  useEffect(() => {
    if (menuOpen === 'none') return;
    const onMouseDown = (e: MouseEvent) => {
      const anchor = menuAnchorRef.current;
      if (!anchor) return;
      if (e.target instanceof Node && !anchor.contains(e.target)) {
        // Don't close if the click was on the gear / cc button itself — those
        // already toggle. Heuristic: any element with role="menuitem" or
        // matching the chrome buttons would land in here too. Cleanest is
        // to let the button's own onClick toggle and ignore the document
        // listener for one frame after a button click. We approximate by
        // checking if the click landed on something that has the gear/cc
        // aria-label.
        const el = e.target as HTMLElement;
        const label = el.closest?.('button')?.getAttribute('aria-label') || '';
        if (label === 'Settings' || label === 'Captions') return;
        setMenuOpen('none');
        setSettingsView('root');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [menuOpen]);

  // ── Auto-play next countdown ────────────────────────────────────────────
  useEffect(() => {
    if (!endedShown || !nextUp || nextUp.length === 0 || !onPlayNext || autoPlayNextSec <= 0) {
      setAutoNextRemaining(0);
      return;
    }
    setAutoNextRemaining(autoPlayNextSec);
    const id = window.setInterval(() => {
      setAutoNextRemaining((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          onPlayNext(nextUp[0].id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [endedShown, nextUp, onPlayNext, autoPlayNextSec]);

  const cancelAutoNext = useCallback(() => setAutoNextRemaining(0), []);

  const effectiveVolume = muted ? 0 : volume;
  const showBigPlay = !playing && !started && !loading && !endedShown;
  const showPausedPill = !playing && started && !loading && !endedShown;

  const dataState =
    endedShown ? 'ended' :
    loading ? 'buffering' :
    !started ? 'idle' :
    scrubbing ? 'scrubbing' :
    menuOpen === 'settings' ? 'settings' :
    menuOpen === 'captions' ? 'captions' :
    playing ? (showChrome ? 'hover' : 'playing') :
    'paused';

  const wrapperStyle: React.CSSProperties = {
    ['--accent' as string]: accent,
    ['--radius' as string]: `${radius}px`,
    ['--pad' as string]: '18px',
    ['--gap' as string]: '10px',
    borderRadius: radius,
    // `--ambient` drives both the letterbox-bar tint (.oden-root background)
    // and the outer halo (.oden-root box-shadow), each with its own eased
    // transition. Cleared (and CSS falls back to #0a0908) when ambient mode
    // isn't active or the canvas reads were blocked.
    ...(ambientActive ? { ['--ambient' as string]: ambientColor } : {}),
  } as React.CSSProperties;

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      role="region"
      aria-label={title ? `Video: ${title}` : 'Video player'}
      onKeyDown={onKeyDown}
      onMouseMove={bumpControls}
      className={`oden-root oden-theme-${resolvedTheme} oden-ctrl-bar w-full h-full focus:outline-none`}
      style={wrapperStyle}
      data-state={dataState}
    >
      <video
        ref={videoRef}
        playsInline
        preload="metadata"
        autoPlay={autoPlay}
        poster={poster}
        // crossOrigin="anonymous" lets the ambient hook read frames via
        // getImageData. Requires the bucket to send Access-Control-Allow-
        // Origin (configured on nni-drive-bucket as of Apr 2026). If a
        // future video source rejects CORS, the hook detects the
        // SecurityError on first read, sets `tainted=true`, and the wrapper
        // falls back to the solid #0a0908 background.
        crossOrigin={ambientMode ? 'anonymous' : undefined}
        onClick={handleVideoClick}
        // No solid background here — we want the wrapper's --ambient-driven
        // background to show through in letterbox bars.
        className="absolute inset-0 w-full h-full object-contain"
        style={{ zIndex: 1 }}
      >
        <source src={src} type={sourceType} />
        {captions?.map((c, i) => (
          <track
            key={c.id}
            kind="subtitles"
            src={c.src}
            srcLang={c.lang}
            label={c.label}
            default={i === 0 && c.default}
          />
        ))}
        Your browser does not support inline video playback.
      </video>

      {doubleTapHint && (
        <div
          key={doubleTapHint.key}
          className={`oden-dtap oden-dtap-${doubleTapHint.side}`}
          aria-hidden="true"
        >
          <div className="oden-dtap-ripple" />
          <div className="oden-dtap-badge">
            <span className="oden-dtap-glyph">
              {doubleTapHint.side === 'left' ? <Back5Icon size={26} /> : <Fwd5Icon size={26} />}
            </span>
            <span className="oden-dtap-label">5s</span>
          </div>
        </div>
      )}

      {(showChrome || !started || loading || endedShown) && <div className="oden-vignette" />}

      {/* Top bar with optional title / cast / share */}
      {(showChrome || !started) && (sc.title || sc.cast || sc.share) && (
        <div className="oden-topbar" style={{ padding: 'var(--pad)' }}>
          {sc.title && (title || subtitle) && (
            <div className="oden-title">
              {title && <div className="oden-title-main">{title}</div>}
              {subtitle && <div className="oden-title-sub">{subtitle}</div>}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <div className="oden-topbar-actions">
            {sc.cast && (
              <GlassBtn size="sm" label="Cast" tooltipPlacement="bottom" onClick={handleCast}>
                <Cast size={14} />
              </GlassBtn>
            )}
            {sc.share && (
              <GlassBtn size="sm" label="Share" tooltipPlacement="bottom" onClick={handleShare}>
                <Share2 size={14} />
              </GlassBtn>
            )}
          </div>
        </div>
      )}

      {/* Center overlays */}
      {showBigPlay && (
        <div className="oden-center">
          <button type="button" className="oden-bigplay" onClick={togglePlay} aria-label="Play">
            <span style={{ width: 36, height: 36, display: 'inline-flex' }}>
              <Play size={36} fill="currentColor" />
            </span>
          </button>
          {duration > 0 && (
            <div className="oden-idle-meta">
              <div className="oden-idle-len">{formatTime(duration)}</div>
            </div>
          )}
        </div>
      )}

      {showPausedPill && (
        <div className="oden-center oden-paused-mark">
          <button type="button" onClick={togglePlay} className="oden-paused-pill" aria-label="Resume">
            <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
              <Pause size={14} fill="currentColor" />
            </span>
            <span>Paused</span>
          </button>
        </div>
      )}

      {loading && !endedShown && (
        <div className="oden-center" aria-hidden="true">
          <div className="oden-spinner" />
          <div className="oden-buffer-tag">Buffering</div>
        </div>
      )}

      {/* Custom caption rendering — drawn from cuechange so we can theme the pill. */}
      {captionLine && !endedShown && (
        <div className="oden-caption">
          <span>{captionLine}</span>
        </div>
      )}

      {/* Settings + captions menus */}
      {menuOpen !== 'none' && (
        <div ref={menuAnchorRef} className="oden-menu-anchor" data-which={menuOpen}>
          {menuOpen === 'settings' ? (
            <OdenSettingsMenu
              view={settingsView}
              onChangeView={(v) => setSettingsView(v)}
              onClose={() => { setMenuOpen('none'); setSettingsView('root'); }}
              speeds={speeds}
              currentSpeed={playbackRate}
              onSelectSpeed={(rate) => { setRate(rate); setSettingsView('root'); }}
              qualities={qualities}
              currentQuality={activeQuality}
              onSelectQuality={(id) => { setActiveQuality(id); onSelectQuality?.(id); setSettingsView('root'); }}
              audioTracks={audioTracksProp}
              currentAudio={activeAudio}
              onSelectAudio={(id) => { setActiveAudio(id); onSelectAudio?.(id); setSettingsView('root'); }}
              captions={captions?.map<CaptionOption>((c) => ({ id: c.id, label: c.label, sub: c.sub }))}
              currentCaption={activeCaption}
              onOpenCaptions={ccAvailable ? () => { setMenuOpen('captions'); } : undefined}
              stats={stats}
              onOpenShortcuts={() => { setMenuOpen('none'); setSettingsView('root'); setShortcutsOpen(true); }}
            />
          ) : (
            <OdenCaptionsMenu
              tracks={captions ?? []}
              current={activeCaption}
              onSelect={(id) => setActiveCaption(id)}
              onClose={() => setMenuOpen('none')}
            />
          )}
        </div>
      )}

      {/* Shortcuts overlay */}
      <OdenShortcuts open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Ended state */}
      {endedShown && (
        <div className="oden-ended" style={{ gridTemplateColumns: nextUp && nextUp.length > 0 ? '1.1fr 1fr' : '1fr' }}>
          <div className="oden-ended-left">
            <div className="oden-ended-eyebrow">Just watched</div>
            <div className="oden-ended-title">{title ?? 'Video'}</div>
            <div className="oden-ended-actions">
              <button type="button" className="oden-btn-primary" onClick={handleReplay}>
                <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
                  <RotateCcw size={14} />
                </span>{' '}
                Replay
              </button>
              {sc.share && (
                <button type="button" className="oden-btn-ghost" onClick={handleShare}>
                  <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
                    <Share2 size={14} />
                  </span>{' '}
                  Share
                </button>
              )}
            </div>
          </div>
          {nextUp && nextUp.length > 0 && (
            <div className="oden-ended-next">
              <div className="oden-ended-eyebrow">
                {autoNextRemaining > 0 ? (
                  <>
                    Up next · auto in {autoNextRemaining}s ·{' '}
                    <button type="button" onClick={cancelAutoNext} style={{ background: 'transparent', border: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>
                      cancel
                    </button>
                  </>
                ) : (
                  'Up next'
                )}
              </div>
              {nextUp.slice(0, 2).map((n, idx) => (
                <button
                  key={n.id}
                  type="button"
                  className={`oden-ended-card${idx > 0 ? ' oden-ended-card-dim' : ''}`}
                  onClick={() => { cancelAutoNext(); n.onClick?.(); onPlayNext?.(n.id); }}
                >
                  <div className="oden-ended-card-poster" style={n.posterUrl ? { backgroundImage: `url(${n.posterUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                    {!n.posterUrl && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #2a1f3d, #1a1224)' }} />}
                  </div>
                  <div className="oden-ended-card-meta">
                    <div className="oden-ended-card-title">{n.title}</div>
                    {n.subtitle && <div className="oden-ended-card-sub">{n.subtitle}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Always-visible progress thread — only renders when chrome is hidden
          AND the user isn't in fullscreen (where they've opted into full
          immersion and don't want any persistent UI). */}
      {started && !showChrome && !isFullscreen && !endedShown && duration > 0 && (
        <div className="oden-thread" aria-hidden="true">
          <div
            className="oden-thread-fill"
            style={{ width: `${Math.max(0, Math.min(100, (currentTime / duration) * 100))}%` }}
          />
        </div>
      )}

      {/* Bottom chrome */}
      <div
        className="oden-chrome oden-chrome-bar oden-chrome-fade"
        data-visible={showChrome && !endedShown ? '1' : '0'}
        style={{ padding: 'var(--pad)', gap: 'var(--gap)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {sc.scrub && (
          <SeekBar
            currentTime={currentTime}
            duration={duration}
            buffered={buffered}
            sprite={sprite}
            chapters={chapters}
            onSeek={seekTo}
            onScrubStart={() => setScrubbing(true)}
            onScrubEnd={() => setScrubbing(false)}
          />
        )}
        <div className="oden-chrome-row" style={{ gap: 'var(--gap)' }}>
          <div className="oden-chrome-left" style={{ gap: 'var(--gap)' }}>
            {sc.play && (
              <GlassBtn label={playing ? 'Pause' : 'Play'} shortcut="Space" onClick={togglePlay}>
                <MorphPlayPause playing={playing} size={18} />
              </GlassBtn>
            )}
            {sc.back10 && (
              <GlassBtn label="Back 5 seconds" shortcut="←" onClick={() => seekBy(-5)}>
                <Back5Icon size={18} />
              </GlassBtn>
            )}
            {sc.fwd10 && (
              <GlassBtn label="Forward 5 seconds" shortcut="→" onClick={() => seekBy(5)}>
                <Fwd5Icon size={18} />
              </GlassBtn>
            )}
            {sc.vol && (
              <div className="oden-vol-cluster">
                <GlassBtn label={muted ? 'Unmute' : 'Mute'} shortcut="M" onClick={toggleMute}>
                  {muted || effectiveVolume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </GlassBtn>
                <VolumeBar level={effectiveVolume} onChange={setVolumeAbs} />
              </div>
            )}
            {sc.time && (
              <div className="oden-time">
                <span className="oden-time-cur">{formatTime(currentTime)}</span>
                <span className="oden-time-sep">/</span>
                <span className="oden-time-tot">{formatTime(duration)}</span>
              </div>
            )}
          </div>
          <div className="oden-chrome-right" style={{ gap: 'var(--gap)' }}>
            {sc.cc && (
              <GlassBtn
                label="Captions"
                shortcut="C"
                active={menuOpen === 'captions' || activeCaption !== null}
                onClick={() => setMenuOpen((m) => (m === 'captions' ? 'none' : 'captions'))}
              >
                <Subtitles size={18} />
              </GlassBtn>
            )}
            {sc.settings && (
              <GlassBtn
                label="Settings"
                active={menuOpen === 'settings'}
                onClick={() => {
                  setMenuOpen((m) => (m === 'settings' ? 'none' : 'settings'));
                  setSettingsView('root');
                }}
              >
                <SettingsIcon size={18} />
              </GlassBtn>
            )}
            {sc.pip && (
              <GlassBtn label="Picture in picture" shortcut="I" active={isPip} onClick={togglePip}>
                <PictureInPicture2 size={18} />
              </GlassBtn>
            )}
            {sc.fs && (
              <GlassBtn label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} shortcut="F" onClick={toggleFullscreen}>
                {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
              </GlassBtn>
            )}
          </div>
        </div>
      </div>

      {/* Fallback inline loader for very large initial buffer (helps when the
          'waiting' event hasn't fired yet but the video has no readyState). */}
      {!started && !showBigPlay && !endedShown && (
        <div className="oden-center" aria-hidden="true">
          <Loader2 size={36} className="text-white animate-spin" />
        </div>
      )}
    </div>
  );
};

export default OdenPlayer;
